import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { useQueueIssues } from 'pages/Desk/issues/issue_details/header/useQueueIssues';
import {
    IssueFilter,
    IssueMode,
    IssuesDefaultSort,
    type PlainIssue,
} from 'pages/Desk/issues/shared';
import { useDeskValues } from 'pages/Desk/issues/state/DeskProvider';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { isObject } from 'utils/object-validation';
import {
    getIssueDetailsRoute,
    getLocationMachineHealthDeskBaseRoute,
} from 'utils/RouteHelper';
import { deserializeBase64Json, serializeBase64Json } from 'utils/urlSerialization';
import { useDefer } from 'utils/useDefer';
import { useQueryParams } from 'utils/useQueryParams';

const QUEUE_TYPE_DRAFT = 'draft';
const URL_CLEAR_DELAY_MS = 10;
const QUEUE_CACHE_TIME_MS = 30000;

type QueueParams = {
    queueType: string | null;
    mode: IssueMode;
    filter: IssueFilter | null;
    sort: DataSourceSort[];
    captured: boolean;
};

function serializeFilter(filter: IssueFilter): string {
    return serializeBase64Json(filter);
}

/**
 * Type guard to check if a value is a record-like object.
 *
 * @param value - The value to check
 * @returns True if the value is a non-null object, false otherwise
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return isObject(value) && !Array.isArray(value) && value !== null;
}

function deserializeFilter(serialized: string | null): IssueFilter | null {
    return deserializeBase64Json(serialized, null, (parsed) => {
        if (!isRecord(parsed)) {
            return null;
        }
        return parsed;
    });
}

function serializeSort(sort: DataSourceSort[]): string {
    return serializeBase64Json(sort);
}

function deserializeSort(serialized: string | null): DataSourceSort[] {
    return deserializeBase64Json(serialized, IssuesDefaultSort);
}

/**
 * Creates query string parameters for draft queue navigation.
 * Serializes filter and sort parameters to maintain queue state.
 *
 * @param mode - The issue mode ('machineHealth' or 'hardwareHealth')
 * @param filter - The current filter configuration
 * @param sort - The current sort configuration
 * @returns Query string starting with '?'
 */
export function createDraftQueueParams(
    mode: IssueMode,
    filter: IssueFilter,
    sort: DataSourceSort[],
): string {
    const serializedFilter = serializeFilter(filter);
    const serializedSort = serializeSort(sort);

    const urlParams = new URLSearchParams({
        queue: QUEUE_TYPE_DRAFT,
        mode,
        filter: serializedFilter,
        sort: serializedSort,
    });

    return `?${urlParams.toString()}`;
}

/**
 * Safely accesses queue-related state from DeskProvider.
 * Returns null values if DeskProvider context is not available (e.g., on location overview pages).
 *
 * @returns Object containing queuedIssueIds array and setQueuedIssueIds function, or fallback values
 */
export function useDeskQueueState() {
    try {
        return useDeskValues((state) => ({
            queuedIssueIds: state.queuedIssueIds,
            setQueuedIssueIds: state.setQueuedIssueIds,
        }));
    } catch {
        return {
            queuedIssueIds: [] as string[],
            setQueuedIssueIds: null as ((ids: string[]) => void) | null,
        };
    }
}

/**
 * Initializes a draft issue queue from URL parameters when loading an issue details page.
 *
 * This hook reads queue parameters from the URL (queue=draft&filter=...&sort=...) and uses
 * useQueueIssues to fetch current draft issues based on the filter/sort criteria.
 *
 * The hook is context-aware:
 * - When no queue exists in DeskProvider (e.g., navigating from location overview):
 *   Initializes queue from URL parameters
 * - When queue already exists in DeskProvider (e.g., navigating within desk context):
 *   Skips initialization to preserve existing queue state
 *
 * The hook uses useRef to capture URL parameters immediately before other components
 * can clear them, then clears the URL after a short delay to prevent interference.
 *
 * Safe to call from any context - gracefully handles missing DeskProvider.
 */
export function useQueueInitialization() {
    const history = useHistory();
    const queryParams = useQueryParams();
    const { queuedIssueIds, setQueuedIssueIds } = useDeskQueueState();
    const defer = useDefer();

    const queueParamsRef = useRef<QueueParams>({
        queueType: null,
        mode: 'machineHealth',
        filter: null,
        sort: IssuesDefaultSort,
        captured: false,
    });

    if (!queueParamsRef.current.captured) {
        const queueType = queryParams.get('queue');
        const modeParam = queryParams.get('mode');
        const mode: IssueMode =
            modeParam === 'hardwareHealth' ? 'hardwareHealth' : 'machineHealth';
        const serializedFilter = queryParams.get('filter');
        const filter = deserializeFilter(serializedFilter);
        const sort = deserializeSort(queryParams.get('sort'));

        queueParamsRef.current = {
            queueType,
            mode,
            filter,
            sort,
            captured: true,
        };

        if (queueType) {
            defer(() => {
                history.replace({ search: '' });
            }, URL_CLEAR_DELAY_MS);
        }
    }

    const { queueType, mode, filter: finalFilter, sort } = queueParamsRef.current;

    const shouldInitializeFromUrl = queuedIssueIds.length === 0;
    const isQueueActive =
        queueType === QUEUE_TYPE_DRAFT && finalFilter !== null && shouldInitializeFromUrl;

    const { data: issueIds } = useQueueIssues({
        mode,
        filter: finalFilter || {},
        sort,
        options: {
            enabled: isQueueActive,
            staleTime: QUEUE_CACHE_TIME_MS,
        },
    });

    useEffect(() => {
        if (isQueueActive && issueIds && setQueuedIssueIds) {
            setQueuedIssueIds(issueIds);
        }
    }, [isQueueActive, issueIds, setQueuedIssueIds]);
}

/**
 * Handles auto-advancing to the next issue in a draft queue after publish/delete actions.
 *
 * This hook provides functionality to automatically navigate to the next draft issue
 * in a queue when the current issue is published or deleted. If no more issues remain,
 * it navigates to the draft issues list with a completion message.
 *
 * The returned function removes the current issue from the queue and either navigates
 * to the next issue or returns to the draft issues list.
 *
 * @param issue - The current issue being processed
 * @param queuedIssueIds - Array of issue IDs in the current queue
 * @param setQueuedIssueIds - Function to update the queued issue IDs (null if not available)
 * @param push - React Router push function for navigation
 * @returns Function to trigger auto-advance behavior
 */
export function useAutoAdvanceQueue(
    issue: PlainIssue,
    queuedIssueIds: string[],
    setQueuedIssueIds: ((ids: string[]) => void) | null,
    push: ReturnType<typeof useHistory>['push'],
) {
    return () => {
        const currentIndex = queuedIssueIds.indexOf(issue.Id);

        if (currentIndex >= 0 && setQueuedIssueIds) {
            const nextIssueId =
                currentIndex < queuedIssueIds.length - 1
                    ? queuedIssueIds[currentIndex + 1]
                    : null;

            const newQueue = queuedIssueIds.filter((id) => id !== issue.Id);
            setQueuedIssueIds(newQueue);

            if (nextIssueId) {
                push(getIssueDetailsRoute(issue.Location.LocationId, nextIssueId));
            } else {
                const draftUrl = getLocationMachineHealthDeskBaseRoute(
                    issue.Location.LocationId,
                    'draft',
                );
                push(draftUrl, { message: 'No draft issues remaining' });
            }
        }
    };
}
